import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { WarehouseListDocument } from "@/graphql/codegen/graphql"
import type {
  Query,
  WarehouseAreaListFilter,
  WarehouseAreaOutputList,
  WarehouseAreaQueryListArgs,
} from "@/graphql/codegen/graphql"

type UseGetWarehousesParams = WarehouseAreaListFilter & {
  isPaused?: boolean
}

interface UseGetWarehousesReturn {
  warehouses: WarehouseAreaOutputList[]
  isFetching: boolean
  error?: CombinedError
  loadMore: () => void
  getWithReset: () => void
  reset: () => void
}

const LIMIT = 20

export const useGetWarehouses = ({ isPaused = false, ...filter }: UseGetWarehousesParams): UseGetWarehousesReturn => {
  const [warehouses, setWarehouses] = useState<WarehouseAreaOutputList[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<
    Pick<Query, "warehouseArea">,
    WarehouseAreaQueryListArgs
  >({
    query: WarehouseListDocument,
    variables: { input: { filter, limit: LIMIT, offset } },
    pause: isPaused,
  })

  useEffect(() => {
    if (data?.warehouseArea.list.__typename === "WarehouseAreaListSuccess") {
      const paginatedWarehouses = data.warehouseArea.list.warehouseAreas
      setWarehouses((prev: WarehouseAreaOutputList[]) =>
        offset === 0 ? paginatedWarehouses : [...prev, ...paginatedWarehouses]
      )
      if (paginatedWarehouses.length < LIMIT) {
        setHasNextPage(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMore = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
  }

  const reset = () => {
    setOffset(0)
    setWarehouses([])
    setHasNextPage(true)
  }

  const getWithReset = () => {
    reset()
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return {
    warehouses,
    isFetching: fetching,
    error,
    loadMore,
    getWithReset,
    reset,
  }
}
